/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {UseMutationResult, UseQueryResult} from 'react-query/types';
import {cloneDeep} from 'lodash';
import isEmpty from 'lodash.isempty';
import {getLatestTime} from '@ion/core/src/entities/Asset';
import {error} from '@ion/core/src/logger/logger';
import {ALIGNED_ROUTES_CONFIG, massRouteMatching} from '@ion/core/src/services/Here';
import {
  createAlignedNodes,
  createCSV,
  createPathArray
} from '@ion/core/src/services/HereUtils';
import {isNotFoundError, isPermissionsError, isUnauthorizedError} from '@ion/core/src/services/ServiceErrors';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {getQueryClient} from '../config/client';
import {QueryKey} from '../config/QueryKeys';
import {UseDefaultQueryResult, UseMutationQueryResult} from '../hooks/types';

// defining the param definitions for cacheOnFetch function
export interface CacheOnFetchParams {
  queryKey: QueryKey | any[],
  queryFn: Function,
  staleTime?: number,
  defaultErrorHandler?: boolean,
  enabled?: boolean
};

/**
 * The `createQueryResult` helper provides a utility which enhances a React
 * Query UseQueryResult with additional properties; specifically, it adds a
 * disabled and enabled property, which are mapped directly to `isFetching`
 * and `!isFetching`, respectively.
 *
 * In addition, context specific implementation can also be provided which
 * allow for, for example, mapping `query.data` to a specific type; e.g.
 * `users: User[] = data`.
 *
 * ```typescript
 *
 *   return createQueryResult(query, {
 *     users : query.data?.users,
 *     paging,
 *   }) as UseGetUsersResult;
 *
 * ```
 *
 */
export const createQueryResult = <T>(
  query: UseQueryResult,
  result: Partial<UseDefaultQueryResult> & T): Partial<UseDefaultQueryResult> & T => {
  const {isFetching, isLoading, data} = query;
  return {
    ...query,
    ...result,
    loading  : isLoading || (!data && isFetching),
    disabled : isFetching,
    enabled  : result?.enabled ?? !isFetching
  };
};

/**
 * The `createMutationResult` helper provides a utility which enhances a React
 * Query UseMutationQueryResult with additional properties; specifically, it adds
 * disabled and enabled properties, which are mapped directly to `isLoading` and
 * `!isFetching`, respectively.
 *
 * In addition, context specific implementation can also be provided which allow
 * for, for example, mapping `query.data` to a specific type; e.g. `users: User[]
 *  = data`.
 *
 * ```typescript
 *
 *   return createMutationResult(query, {
 *     users : query.data?.users,
 *     paging,
 *   }) as UseGetUsersResult;
 *
 * ```
 *
 */
export const createMutationResult = <T>(
  mutation: UseMutationResult,
  result: Partial<UseMutationQueryResult> & T): Partial<UseMutationQueryResult> & T => {
  return {
    ...mutation,
    ...result,
    disabled : mutation.isLoading,
    enabled  : !mutation.isLoading
  };
};

/**
 * Defines the React Query duration specific configuration properties; either
 * 'staleTime' or 'cacheTime'.
 */
export type DurationProperty = 'staleTime' | 'cacheTime';

/**
 * Defines the `getDuration` argument / parameter types.
 */
type DurationParams = {
  property: DurationProperty;
  duration: number;
  useSeconds: boolean;
  returnObject: boolean;
}

/**
 * Defines the `getStaleTime` and `getCacheTime` duration specific configuration
 * results.
 */
export type DurationResult = {
  staleTime?: number
  cacheTime?: number
}

/**
 * @private
 */
const getDuration = (
  {property, duration, useSeconds = false, returnObject = true}: DurationParams): DurationResult | number => {
  const time   = !useSeconds ? duration * 60 : duration;
  const result = time * 1000;
  if (returnObject) {
    return {[property]: result};
  }
  return result;
};

/**
 * Provides a utility which allows for defining a query's staleTime in minutes
 * (default) or seconds.
 *
 * This utility effectively eliminates the need for converting milliseconds
 * to seconds and / or minutes, effectively encapsulating the basic arithmetic
 * operations for performing conversions.
 *
 * The following example demonstrates using `getStaleTime` to set a staleTime
 * of 5 minutes for a query, which will result in the query being configured
 * with the following options:
 *
 *  - staleTime: 300000 (5 minutes)
 *  - keepPreviousData: true
 *
 * ```typescript
 *
 *   // set 'staleTime' option to 5 minutes ...
 *   const query = useQuery(keys, getUsers, {
 *     ...getStaleTime(5),
 *     keepPreviousData: true
 *   });
 *
 *   // set 'staleTime' option to 30 seconds ...
 *   const query = useQuery(keys, getNotifications, {
 *     ...getStaleTime(30, true),
 *     keepPreviousData: true
 *   });
 *
 * ```
 *
 */
export const getStaleTime = (
  duration: number, useSeconds = false, returnObject = true): DurationResult | number => {
  return getDuration({property: 'staleTime', duration, useSeconds, returnObject});
};

/**
 * Provides a utility which allows for defining a queries cacheTime in minutes
 * (default) or seconds.
 *
 * This utility effectively eliminates the need for converting milliseconds
 * to seconds and / or minutes, effectively encapsulating the basic arithmetic
 * operations for performing conversions.
 *
 * The following example demonstrates using `getCacheTime` to set a cacheTime
 * of 5 minutes for a query, which will result in the query being configured
 * with the following options:
 *
 *  - staleTime: 300000 (5 minutes)
 *  - keepPreviousData: true
 *
 * ```typescript
 *
 *   // set 'cacheTime' option to 30 minutes ...
 *   const query = useQuery(keys, getUsers, {
 *     ...getStaleTime(30),
 *     keepPreviousData: true
 *   });
 *
 *   // set 'cacheTime' option to 5 seconds ...
 *   const query = useQuery(keys, getAlerts, {
 *     ...getStaleTime(5, true),
 *     keepPreviousData: true
 *   });
 * ```
 *
 */
export const getCacheTime = (
  duration: number, useSeconds = false, returnObject = true): DurationResult | number => {
  return getDuration({property: 'cacheTime', duration, useSeconds, returnObject});
};

/**
 * Invalidates the query cache of the provided query key.
 *
 * ```typescript
 *
 * import {invalidateQueryCache} from '@ion/query/src/utils';
 *
 * invalidateQueryCache(QueryKey.LOGIN); // Will erase the LOGIN query's cache
 * invalidateQueryCache([QueryKey.DEVICE_GET, id]); // Will erase the DEVICE_GET query's cache with the given id
 *
 * ```
 */
export const invalidateQueryCache = (key: QueryKey | (QueryKey | number | string)[]) => {
  getQueryClient().invalidateQueries(key);
};

// [CORE PACKAGE TODO]: THIS UTILITY IS NOT A GENERIC QUERY UTILITY, THIS IS TO BE
// REFACTORED TO @ion/core/src/utils/NumberUtils.ts
/**
 * getNumberDecimal rounds a number to the specified number of decimal places.
 *
 * ```typescript
 * import {getNumberDecimal} from '@ion/core/src/utils/';
 *
 * getNumberDecimal(0.123456789, 3); // 0.123
 * getNumberDecimal(0.123456789, 0); // 0
 * getNumberDecimal(26.123456789, 1); // 26.1
 *
 * ```
 */
export const getNumberDecimal = (value: number, precision = 1): number => {
  const multiplier = Math.pow(10, precision);
  return Math.round(value * multiplier) / multiplier;
};

/**
 * if we have a client error, we should not throw it because that will leads
 * react-query to do retry, which is not required for client errors.If we
 * have any other type of error, we should throw it direclty to the react-query
 * inorder to do retries.
 *
 * ```typescript
 *
 *  import {onClientErrorHandler} from '@ion/query/src/utils/helpers';
 *
 *  try {
 *    const response = await api();
 *  } catch (e) {
 *    return onClientErrorHandler(e);
 *  }
 *
 * ```
 */
export const onClientErrorHandler = (error) => {
  const {errorCode}   = error;
  const isClientError = (
    isUnauthorizedError(errorCode) ||
    isPermissionsError(errorCode) ||
    isNotFoundError(errorCode)
  );
  if (isClientError) {
    return error;
  }
  throw error;
};

/**
 * Fetches data from the server and caches it using the React Query cache.
 *
 *```typescript
 *
 * const data = await cacheOnFetch({
 *   queryKey  : 'my-data', // Unique key for the cached data
 *   queryFn   : () => fetch('/api/data'), // Function to fetch data from the server
 *   staleTime : 5000, // Optional, custom stale time in milliseconds (default: 1 minute)
 * });
 *
 * ```
 */
export const cacheOnFetch = async({
  queryKey,
  queryFn,
  enabled = true,
  defaultErrorHandler = false,
  staleTime = getStaleTime(1, false, false) as number
}: CacheOnFetchParams) => {
  if (enabled) {
    const queryClient = getQueryClient();
    try {
      const response = await queryClient.fetchQuery({
        queryFn : queryFn as any,
        queryKey,
        staleTime
      });
      return response;
    } catch (err) {
      if (defaultErrorHandler) {
        return null;
      } else {
        throw new Error(err);
      }
    }
  }
  return null;
};

/**
 * Removes all queries with keys starting with the specified base key from the query cache.
 * 
 * This function is useful for selectively removing or refetching queries from the query cache
 * based on a common base key. It iterates through all queries in the query cache, matches
 * queries whose keys start with the specified base key, and either removes them from the cache
 * or refetches them based on the provided parameters.
 * 
 * ```typescript
 * 
 *  import {removeQueries} from '@ion/query/scr/utils/helpers';
 * 
 *  // Remove all queries related to a specific resource type when it's deleted
 *  const removeResourceQueries = (resourceType: string) => {
 *     removeQueries(`resource:${resourceType}`);
 *  };
 * 
 *  // Refetch all active queries related to a specific resource type when it's updated
 *  const updateResource = (resourceType: string) => {
 *    removeQueries(`resource:${resourceType}`, true);
 *  };
 * 
 * ```
 * 
 * @param baseKey The base key used to match queries for removal.
 *                Queries whose keys start with this base key will be targeted for removal or refetching.
 * @param refetchIfActive Optional. If set to true, active queries matching the base key will be refetched
 *                        instead of removed. This is useful when you want to update the data for active queries.
 *                        Defaults to true.
 */

export const removeQueries = (baseKey: QueryKey, refetchIfActive = true) => {
  const queryClient = getQueryClient();
  const queryCache  = queryClient.getQueryCache();
  const queries     = queryCache.findAll();
  queries.forEach(query => {
    if (
      (query.queryKey?.[0] as string)?.startsWith(baseKey) ||
      safeJSONParse(query.queryKey as string)?.[0]?.startsWith(baseKey)
    ) {
      if (refetchIfActive && query.isActive()) {
        queryClient.refetchQueries(query.queryKey);
      } else {
        queryClient.removeQueries(query.queryKey);
      }
    }
  });
};

/**
 * Defines a utility to return last time based on given params.
 * 
 * import {getLastTime} from '@ion/query/scr/utils/helpers';
 * 
 * const time = getLastTime(pages, 'time');
 * 
 */
export const getLastTime = (pages: any[], propertyTime: string) => {
  const flattenedData = pages?.flat();
  if (flattenedData?.length) {
    const lastTime = getLatestTime(flattenedData as any, propertyTime);
    return lastTime || null;
  }
  return new Date().getTime();
};

/**
 * getAlignedRoutes method checkes cached response for
 * given requestBody and make a request to HERE api if not stored.
 * It will update the redux store to cache the response from HERE api
 * for later usage.
 * It returns an updated routeHistory only if needed.
 *
 * ```typescript
 * import {getAlignedRoutes} from '@ion/query/scr/utils/helpers';
 *
 * ...
 *
 * ```
 *
 */
export const getAlignedRoutes = async(assetsMap, routeHistory) => {
  const {
    ALIGNED_ROUTES_CSV_HEADERS,
    ALIGNED_ROUTES_MAX_DISTANCE
  } = ALIGNED_ROUTES_CONFIG;
  const requestMap = {};
  const state      = cloneDeep(routeHistory);

  // loop through the assetsMap and check if request with same coordinates is
  // cached in the redux store, if not, assign request to the requestMap
  for (const key in assetsMap) {
    const requestBody = createCSV(assetsMap[key], ALIGNED_ROUTES_CSV_HEADERS);
    const result      = requestBody? routeHistory[requestBody] : null;
    if (!result) {
      requestMap[key] = assetsMap[key];
    }
  }
  try {
    if (!isEmpty(requestMap)) {
      const responses = (await massRouteMatching(requestMap) as any).map(({value}) => value);
      if (responses) {
        for (const asset of responses) {
          const {response, assetId} = asset;
          const nodes        = await createAlignedNodes(response, assetsMap[assetId], ALIGNED_ROUTES_MAX_DISTANCE);
          const routes       = createPathArray(response, nodes);
          const requestBody  = createCSV(assetsMap[assetId], ALIGNED_ROUTES_CSV_HEADERS);
          state[requestBody] = {nodes, routes};
        }
        return state;
      }
    }
  } catch (err) {
    error(err);
  }
};
